import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Tabs, Tab } from '@mui/material';
import { Alert, Loading } from '../../../components';
import { useGetAllOrganizations } from './graphql/useGetAllOrganizations';
import { DepartmentView } from './DepartmentView';
import { HeadView } from './HeadView';
import { MemberView } from './MemberView';

enum OrganizationViewType {
  DEPARTMENT = 'DEPARTMENT',
  HEAD = 'HEAD',
  MEMBER = 'MEMBER',
}

export const OrganizationManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, loading, error } = useGetAllOrganizations();

  const [activeTab, setActiveTab] = useState<OrganizationViewType>(
    OrganizationViewType.DEPARTMENT,
  );

  // State to manage alert visibility and content
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<{
    alertContent: string;
    alertType: 'success' | 'error';
  }>({
    alertContent: '',
    alertType: 'success',
  });

  useEffect(() => {
    if (location?.state?.alertContent != null) {
      setAlertDetails({
        alertContent: location.state.alertContent,
        alertType: location.state.alertType ?? 'success',
      });
      setOpenAlert(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: OrganizationViewType,
  ): void => {
    setActiveTab(newValue);
  };

  const handleAddFacultyClick = (): void => {
    navigate('/internal/super-admin/organization-management/faculty/add');
  };

  const handleEditFacultyClick = (id: string): void => {
    navigate(
      `/internal/super-admin/organization-management/faculty/edit/${id}`,
    );
  };

  const handleAddDepartmentClick = (facultyId: string): void => {
    navigate(
      `/internal/super-admin/organization-management/department/add?facultyId=${facultyId}`,
    );
  };

  const handleEditDepartmentClick = (id: string, facultyId: string): void => {
    navigate(
      `/internal/super-admin/organization-management/department/edit/${id}?facultyId=${facultyId}`,
    );
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs: ${error instanceof Error ? error.message : 'Unknown error'}`,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <div>
      <header className="flex justify-center py-6">
        <Typography variant="h4">
          Administrative Department Management
        </Typography>
      </header>

      <Tabs value={activeTab} onChange={handleTabChange} centered>
        <Tab label="Department View" value={OrganizationViewType.DEPARTMENT} />
        <Tab label="Dean/Head View" value={OrganizationViewType.HEAD} />
        <Tab label="D/S/FRC/SB View" value={OrganizationViewType.MEMBER} />
      </Tabs>

      {activeTab === OrganizationViewType.DEPARTMENT && (
        <DepartmentView
          data={data?.getAllOrganizations ?? []}
          onAddFacultyClick={handleAddFacultyClick}
          onEditFacultyClick={handleEditFacultyClick}
          onAddDepartmentClick={handleAddDepartmentClick}
          onEditDepartmentClick={handleEditDepartmentClick}
        />
      )}

      {activeTab === OrganizationViewType.HEAD && (
        <HeadView
          data={data?.getAllOrganizations ?? []}
          onEditClick={(
            id: string,
            isDepartment: boolean = false,
            facultyId?: string,
          ) => {
            if (
              isDepartment &&
              typeof facultyId === 'string' &&
              facultyId.trim() !== ''
            ) {
              handleEditDepartmentClick(id, facultyId);
            } else {
              handleEditFacultyClick(id);
            }
          }}
        />
      )}

      {activeTab === OrganizationViewType.MEMBER && (
        <MemberView
          data={data?.getAllOrganizations ?? []}
          onEditClick={(
            id: string,
            isDepartment: boolean = false,
            facultyId?: string,
          ) => {
            if (
              isDepartment &&
              facultyId === 'string' &&
              facultyId.trim() !== ''
            ) {
              handleEditDepartmentClick(id, facultyId);
            } else {
              handleEditFacultyClick(id);
            }
          }}
        />
      )}

      {/* Alert Component */}
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </div>
  );
};
